import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFocusEffect } from '@react-navigation/native';
import { searchDisplayMode } from '@helpers';
import { DOCUMENT_TYPE_TAG_KEY_MAP } from '@components/imageHandling/common';
import { selectDisplayMode } from '@reducers/details';
import { generateImageURL } from '@reducers/blobStorage';
import { TABLE_NAMES } from '@v2/pages/common/constants/base.constants';
import { useTableTools } from '@v2/pages/common/hooks/useTableTools';
import { useImageHandlingViewModelWithContextUpdate } from '@v2/components/imageHandling/hooks/useImageHandlingViewModelWithContextUpdate';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { ImagesDocumentsContentProps } from '@v2/pages/nc-file/details/modules/nc-file/components/ImagesDocumentsContent';

export interface UseManageImagesDocumentsContentParams extends Pick<ImagesDocumentsContentProps, 'documentRetrieveKey' | 'documentRetrieveId'> {}

export const useManageImagesDocumentsContent = ({
  documentRetrieveKey,
  documentRetrieveId,
}: UseManageImagesDocumentsContentParams) => {
  const dispatch = useAppDispatch();
  const displayMode = useSelector(selectDisplayMode);
  const generateImageURLAction = useCallback((...args: any[]) => dispatch(generateImageURL(...args)), [dispatch]);

  const [totalCount, setTotalCount] = useState<number | null>(0);
  const [galleryContainerMeasurements, setGalleryContainerMeasurements] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const [mainContainerMeasurements, setMainContainerMeasurements] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  const { safeIsOperatorPanelInfoScreen, safeIsDetailsScreen } = useNavigationCheck();

  const {
    apiRef,
    columnVisibilityModel,
    setColumnVisibilityModel,
    filter,
    setFilter,
    filterModel,
    setFilterModel,
    orderBy,
    setOrderBy,
    sortModel,
    setSortModel,
    oDataFilter,
    setODataFilter,
  } = useTableTools(TABLE_NAMES.detailsImagesDocuments);

  const imagesAbortRef = useRef<AbortController>(new AbortController());

  const {
    state: { imagesDocuments, selectedIndex, setSelectedIndex, loading, error },
    actions: { loadImagesDocuments, cropImageDocument, removeImageDocument, rotateImageDocument },
  } = useImageHandlingViewModelWithContextUpdate({
    imageContext: { scopeId: DOCUMENT_TYPE_TAG_KEY_MAP?.[documentRetrieveKey], scopeReferenceId: documentRetrieveId },
    retrieveAllDocuments: true,
  });

  useFocusEffect(
    useCallback(() => {
      const controller = new AbortController();
      imagesAbortRef.current = controller;

      (async () => {
        try {
          await loadImagesDocuments();

          const count = imagesDocuments.length;
          setTotalCount(count);
        } catch (e) {
          console.error(e);
        }
      })();

      return () => {
        imagesAbortRef.current.abort();
      };
    }, [documentRetrieveId, documentRetrieveKey])
  );

  const isTableMode = displayMode === searchDisplayMode.tableCompact || displayMode === searchDisplayMode.tableStandard;
  const isGalleryMode = displayMode === searchDisplayMode.gallery;
  const hasValidMeasurements = mainContainerMeasurements.width > 0 && mainContainerMeasurements.height > 0;

  return {
    displayMode,
    generateImageURL: generateImageURLAction,
    totalCount,
    galleryContainerMeasurements,
    setGalleryContainerMeasurements,
    mainContainerMeasurements,
    setMainContainerMeasurements,
    safeIsOperatorPanelInfoScreen,
    safeIsDetailsScreen,
    apiRef,
    columnVisibilityModel,
    setColumnVisibilityModel,
    filter,
    setFilter,
    filterModel,
    setFilterModel,
    orderBy,
    setOrderBy,
    sortModel,
    setSortModel,
    oDataFilter,
    setODataFilter,
    imagesDocuments,
    selectedIndex,
    setSelectedIndex,
    loading,
    error,
    loadImagesDocuments,
    cropImageDocument,
    removeImageDocument,
    rotateImageDocument,
    isTableMode,
    isGalleryMode,
    hasValidMeasurements,
  };
};
